// Framework-free peer-presence policy behind usePeerCursors: merging an
// incoming presence event into the peer list, pruning peers that have gone
// quiet, deriving a stable per-user colour, and deciding when this browser's
// own cursor/selection is worth sending. No React, no WebSocket - unit
// testable directly.

export interface PeerCursor {
  /** This browser tab's own presence identity (a fresh id per mount), not
   * the signed-in user id - two tabs signed in as the same person still show
   * as two peers, matching the old same-browser BroadcastChannel behaviour. */
  id: string;
  name: string;
  color: string;
  position: [number, number, number];
  selection: string[];
}

/** The `data` shape of a `peer_presence` event, as sent by the API's `/ws`. */
export interface PeerPresenceEvent {
  clientId: string;
  uid: string;
  name: string;
  cursor: [number, number, number] | null;
  selection: string[] | null;
}

/** A stable, visually distinct colour per user id (not per tab), so the same
 * creator keeps the same colour across their own tabs and reconnects. */
export function colorForUid(uid: string): string {
  let hash = 0;
  for (let i = 0; i < uid.length; i++) {
    hash = (hash * 31 + uid.charCodeAt(i)) >>> 0;
  }
  return `hsl(${hash % 360}, 70%, 60%)`;
}

/** Upsert an incoming presence event into the peer list; a null cursor means
 * the peer explicitly went idle/away and is removed rather than shown stuck
 * at its last position. */
export function mergePresence(peers: PeerCursor[], event: PeerPresenceEvent): PeerCursor[] {
  const rest = peers.filter((p) => p.id !== event.clientId);
  if (event.cursor === null) return rest;
  return [
    ...rest,
    {
      id: event.clientId,
      name: event.name,
      color: colorForUid(event.uid),
      position: event.cursor,
      selection: event.selection ?? [],
    },
  ];
}

/** Drop peers not heard from within timeoutMs - a closed tab or dropped
 * socket never sends an explicit "gone" message, so staleness is how a
 * ghost peer eventually clears from the overlay. */
export function pruneStale(
  peers: PeerCursor[],
  lastSeenMs: ReadonlyMap<string, number>,
  now: number,
  timeoutMs: number,
): PeerCursor[] {
  return peers.filter((p) => {
    const seen = lastSeenMs.get(p.id);
    return seen !== undefined && now - seen < timeoutMs;
  });
}

export interface PresenceSample {
  cursor: [number, number, number] | null;
  selection: string[];
}

export interface PresenceSendState extends PresenceSample {
  sentAt: number;
}

function positionsEqual(a: PresenceSample['cursor'], b: PresenceSample['cursor']): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

function selectionsEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((id, i) => id === b[i]);
}

/** Sampled (not per-mousemove) throttling for this browser's own outgoing
 * presence: send on real change, and otherwise still resend at heartbeatMs
 * so a stationary-but-connected peer is not pruned as stale by others. */
export function shouldSendPresence(
  last: PresenceSendState | null,
  next: PresenceSample,
  now: number,
  heartbeatMs: number,
): boolean {
  if (!last) return true;
  if (now - last.sentAt >= heartbeatMs) return true;
  return !positionsEqual(last.cursor, next.cursor) || !selectionsEqual(last.selection, next.selection);
}

/** The `/ws` URL to open for editor presence: an explicit override (typically
 * VITE_WS_URL) if set, otherwise derived from the REST API's own origin. */
export function resolvePresenceWsBase(apiUrl: string, override: string | undefined): string {
  if (override) return override;
  return `${apiUrl.replace(/^http/, 'ws').replace(/\/+$/, '')}/ws`;
}

export function presenceSocketUrl(wsBase: string, token: string): string {
  return `${wsBase}?token=${encodeURIComponent(token)}`;
}
